import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './toast.css';

interface ToastProps {
  message: string;
  tone?: 'success' | 'error';
  onDismiss: () => void;
  /** How long it stays before dismissing itself; the Templates toasts' own timings (4s, 6s for errors). */
  durationMs?: number;
}

/**
 * One transient confirmation, top-right over whatever page is showing.
 *
 * Portalled to `<body>` so no ancestor's overflow or stacking context clips it. It dismisses itself, and can
 * be dismissed sooner. Not PrimeReact's `Toast`: no PrimeReact theme is loaded, so that one draws unstyled.
 */
export function Toast({ message, tone = 'success', onDismiss, durationMs }: ToastProps) {
  const timeout = durationMs ?? (tone === 'error' ? 6000 : 4000);

  useEffect(() => {
    const timer = window.setTimeout(onDismiss, timeout);
    return () => {
      window.clearTimeout(timer);
    };
  }, [onDismiss, timeout]);

  return createPortal(
    <div className={`app-toast app-toast--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <i className={`pi ${tone === 'error' ? 'pi-times-circle' : 'pi-check-circle'}`} aria-hidden="true" />
      <span className="app-toast__text">{message}</span>
      <button type="button" className="app-toast__dismiss" aria-label="Dismiss" onClick={onDismiss}>
        <i className="pi pi-times" aria-hidden="true" />
      </button>
    </div>,
    document.body,
  );
}
